// Core authentication types for auth system

export enum UserRole {
  ENTREPRENEUR = 'ENTREPRENEUR',
  SUPERADMIN = 'SUPERADMIN',
  ORG_ADMIN = 'ORG_ADMIN',
  EXPERT = 'EXPERT',
  // The floor of the staff ladder: belongs to an organization but holds no
  // organization-wide access. Everything it sees comes from a grant row, never
  // from this role, so its resource matrix is empty by design.
  MEMBER = 'MEMBER',
}

export enum SocialProvider {
  LINKEDIN = 'linkedin',
  MICROSOFT = 'microsoft',
  GMAIL = 'gmail'
}

// Entrepreneur business states - detailed states for business logic
export enum EntrepreneurState {
  PENDING_LINKEDIN_PARSE = 'PENDING_LINKEDIN_PARSE',
  PENDING_CV_UPLOAD = 'PENDING_CV_UPLOAD',
  PENDING_ONBOARDING = 'PENDING_ONBOARDING',
  PENDING_SUMMARY = 'PENDING_SUMMARY',
  PENDING_PASSED_PRESCORE = 'PENDING_PASSED_PRESCORE',  // Renamed from PENDING_DONE_SUMMARY
  PENDING_FAILED_PRESCORE = 'PENDING_FAILED_PRESCORE',  // New: First pre-score failure
  REJECTED_FAILED_PRESCORE = 'REJECTED_FAILED_PRESCORE',  // New: Second pre-score failure
  PENDING_WITH_NOTES = 'PENDING_WITH_NOTES',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  SUSPENDED = 'SUSPENDED',
  DELETED = 'DELETED'
}

// Admin business states - detailed states for admin logic
export enum AdminState {
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
  PENDING_SETUP = 'PENDING_SETUP'
}

// Entrepreneur profile with business data
export interface EntrepreneurProfile {
  id: string;
  user_id: string;
  status: EntrepreneurState;
  profile_data: {
    first_name?: string;
    last_name?: string;
    full_name?: string;
    headline?: string;
    email?: string;
    linkedin_url?: string;
    summary?: string;
    location?: string;
    industry?: string;
    profile_picture?: string;
    positions?: any[];
    educations?: any[];
    certifications?: any[];
    skills?: string[];
    languages?: string[];
  } | null;
  executive_summary?: string | null;
  pre_score?: number | null;
  post_score?: number | null;
  admin_notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface BaseUser {
  id: string;
  username: string;
  email?: string;
  role: UserRole;
  scopes?: string[];
  created_at?: string;
  updated_at?: string;
}

export interface EntrepreneurUser extends BaseUser {
  role: UserRole.ENTREPRENEUR;
  state?: 'PENDING' | 'PENDING_EMAIL_VERIFICATION' | 'ACTIVE' | 'DISABLED';  // Simplified user state from backend
  entrepreneur_state: EntrepreneurState;  // Detailed entrepreneur state
  linkedin_id?: string;
  linkedin_url?: string;
  prescore_attempts?: number;
  email_verified?: boolean;
  profile_data?: {
    linkedin_id?: string;
    first_name?: string;
    last_name?: string;
    full_name?: string;
    email?: string;
    profile_picture?: string;
    headline?: string;
    summary?: string;
    location?: string;
    industry?: string;
    positions?: any[];
    educations?: any[];
    certifications?: any[];
    skills?: string[];
    languages?: string[];
    raw_profile_data?: any;
  };
  terms_accepted_date?: string;
  privacy_accepted_date?: string;
  communications_consent_date?: string;
  approved_by?: string;
  approved_at?: string;
  state_changed_at?: string;
  state_changed_by?: string;
}

export interface StaffUser extends BaseUser {
  role: UserRole.SUPERADMIN | UserRole.ORG_ADMIN | UserRole.MEMBER | UserRole.EXPERT;
  state?: 'PENDING' | 'ACTIVE' | 'DISABLED';
  admin_state: AdminState;
  scopes?: string[];
  organization_id?: string;
  profile_data?: {
    first_name?: string;
    last_name?: string;
    full_name?: string;
    job_title?: string;
    office_location?: string;
    microsoft_id?: string;
  };
}

export interface ExpertUser extends BaseUser {
  role: UserRole.EXPERT;
  state?: 'PENDING' | 'ACTIVE' | 'DISABLED';
  organization_id?: string;
  profile_data?: {
    first_name?: string;
    last_name?: string;
    full_name?: string;
    sectors?: string[];
  };
}

export type User = EntrepreneurUser | StaffUser | ExpertUser;

// Role groupings, scopes, labels and tool access are fetched from the backend
// (the single source of truth) and exposed via these synchronous helpers, which
// read a cache hydrated on boot. See `@/lib/roleRegistry` + `useRoleRegistry`.
export {
  isStaffRole,
  isExpertRole,
  canViewResource,
  canEditResource,
  accessibleResources,
  canAccessArea,
  getAssignableStaffRoles,
  getRoleScopes,
  getRoleLabel,
  getAllPermissions,
  getPermissionLabels,
} from '@/lib/roleRegistry';

export interface UserWithPermissions {
  user: User;
  permissions: string[];
  org_features: string[];
  platform_accesses?: Array<{
    platform: 'foundershub' | 'gaia';
    status: string;
    is_active: boolean;
    is_disabled: boolean;
    redirect_path: string | null;
  }>;
  // The products this caller is entitled to act on (backend-computed —
  // `AuthService.entitled_platforms`, same rule `/api/org/invitations` and
  // `/api/org/platform-access` already gate on). Drives the org panel's
  // platform-section selector (§8.1); never re-derived on the client.
  platforms?: string[];
}

export interface AuthUrlResponse {
  authorization_url: string;
  state?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

// OAuth provider configuration from API
export interface OAuthProviderConfig {
  provider: string;
  auth_url_endpoint: string;
  button_text: string;
  button_style: string;
  icon_type: string;
}

// Complete login configuration from API
export interface LoginConfig {
  // Page metadata
  title: string;
  description: string;
  header_icon: string;
  
  // Navigation paths
  login_path: string;
  success_path: string;
  
  // Authentication options
  allow_password_login: boolean;
  password_login_endpoint?: string;
  oauth_providers: OAuthProviderConfig[];
  
  // UI text
  footer_text?: string;
  help_email?: string;

  // Signup policy (per the login_configurations row).
  // "open" → self-signup enabled.
  // "invitation_only" → only invitation tokens create accounts.
  // "disabled" → no signup at all.
  signup_mode: 'open' | 'invitation_only' | 'disabled';
}



// Authentication state
export interface AuthState {
  user: User | null;
  permissions: string[];
  org_features: string[];
  // The products this caller is entitled to act on — see UserWithPermissions.
  platforms: string[];
  isAuthenticated: boolean;
  isLoading: boolean;
  isAuthVerified: boolean;
  error: string | null;
  currentRole: UserRole | null;
}

// Authentication actions
export interface AuthActions {
  // Core authentication
  loginWithCredentials: (credentials: LoginCredentials, endpoint: string) => Promise<User>;
  logout: (role?: UserRole) => Promise<void>;
  refreshAuth: () => Promise<void>;
  
  // State management
  setUser: (userData: UserWithPermissions | null) => void;
  clearCredentials: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  // Navigation and redirects
  handleAuthRedirect: (currentPath: string, error?: any) => void;
  redirectToLogin: (role: UserRole, redirectPath?: string, loginUrl?: string) => void;
  redirectToDashboard: (role: UserRole, fallbackPath?: string) => void;
  endInactiveSession: (error: string) => Promise<void>;
  handleAccountNotActive: () => Promise<void>;
  
  // Role-specific helpers
  isRole: (role: UserRole) => boolean;
  hasPermission: (permission: string) => boolean;
}

// Component props for auth components
export interface AuthWrapperProps {
  children: React.ReactNode;
  // Gate by explicit role(s) (entrepreneur portal) OR by per-resource access
  // resolved from the backend role registry (staff areas). Exactly one is used.
  role?: UserRole | UserRole[];
  resource?: string;
  redirectOnUnauthorized?: boolean;
  loginPath?: string;
  requiredFeature?: string;
}

export interface AuthFormProps {
  role: UserRole;
  onSuccess?: (user: User) => void;
  onError?: (error: string) => void;
}
